import React from 'react';
import PropTypes from 'prop-types';
import { Link, browserHistory } from 'react-router';
import RaisedButton from 'material-ui/RaisedButton';
import MenuItem from 'material-ui/MenuItem';
import { TextField, SelectField } from 'redux-form-material-ui';
import { reduxForm, Field } from 'redux-form';
import styled from 'styled-components';
import { localize } from 'react-localize-redux';
import { translate } from 'react-i18next';

import PacketboxStatusFields from './PacketboxStatusFields';
import { required } from '../utils/form-validators';
import { dateFromISO } from '../utils/helpers';
import ChangeOwnerDialog from './ChangeOwnerDialog.js';
import i18n from '../translations/i18n';
/*
  Styled components
 */
const ButtonContainer = styled.div`
  margin-bottom: 10px;
  float: right;
`;

const PlainField = styled.div`
  padding-top: 40px;
  padding-bottom: 16px;
`;

const StyledSubmitButton = styled(RaisedButton)`
  margin-left: 5px !important;
`;

/*
  PropTypes / DefaultProps
 */
const propTypes = {
  isModify: PropTypes.bool,
  handleSubmit: PropTypes.func.isRequired,
  onRegenerateTan: PropTypes.func,
  change: PropTypes.func,
  t: PropTypes.func,
  packetbox: PropTypes.any,
  endUsers: PropTypes.any,
  packetboxKeys: PropTypes.any,
};

const defaultProps = {
  isModify: false,
  onRegenerateTan: () => {},
};

class PacketboxForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      changeOwnerDialogOpen: false,
      owner: null,
    };

    this.currentOwner = null;
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.packetbox && nextProps.endUsers) {
      const currentOwner = nextProps.endUsers.find(
        endUser => endUser.id === nextProps.packetbox.owner_id
      );
      this.setState({
        owner: currentOwner,
      });
    }
  }

  openChangeOwnerDialog = () => {
    this.setState({
      changeOwnerDialogOpen: true,
    });
  };

  closeChangeOwnerDialog = () => {
    this.setState({
      changeOwnerDialogOpen: false,
    });
  };

  changeOwner = (newOwner) => {
    this.setState({
      owner: newOwner,
      changeOwnerDialogOpen: false,
    });

    // Trigger change in redux form store
    this.props.change('owner_id', newOwner.id);
  };

  render() {
    const { handleSubmit, isModify, endUsers, packetbox, packetboxKeys } = this.props;

    const passValidation = isModify ? [required] : null;
    const ownerDisabled = !(packetbox && packetbox.owner_id);
    const codeDisabled = !!isModify;

    const dataSourceConfig = {
      text: 'text',
      value: 'value',
    };

    const dataSource = endUsers.map(endUser => ({
      text: `${endUser.full_name} (${endUser.username})`,
      value: endUser.id,
    }));

    let ownerName = '';

    if (this.state.owner) {
      ownerName = `${this.state.owner.full_name} (${this.state.owner.username})`;
    }

    return (
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-xs-6">
            <div className="box">
              <Field
                component={TextField}
                validate={[required]}
                disabled={codeDisabled}
                name="code"
                hintText={this.props.t('code')}
                floatingLabelText={this.props.t('code')}
                fullWidth
              />

              <Field
                component={TextField}
                validate={[required]}
                name="name"
                hintText={this.props.t('name')}
                floatingLabelText={this.props.t('name')}
                fullWidth
              />

              <TextField
                name="owner"
                disabled
                hintText={this.props.t('owner')}
                floatingLabelText={this.props.t('owner')}
                value={ownerName}
                style={{ marginRight: 20 }}
              />
              <RaisedButton
                label={this.props.t('change_owner')}
                onClick={this.openChangeOwnerDialog}
              />

              <Field
                component={TextField}
                name="street"
                hintText={this.props.t('street')}
                floatingLabelText={this.props.t('street')}
                style={{ width: '50%' }}
              />

              <Field
                component={TextField}
                name="street_number"
                hintText={this.props.t('number')}
                floatingLabelText={this.props.t('number')}
                style={{ width: '40%', marginLeft: '10%' }}
              />

              <Field
                component={TextField}
                name="place"
                hintText={this.props.t('place')}
                floatingLabelText={this.props.t('place')}
                style={{ width: '50%' }}
              />

              <Field
                component={TextField}
                name="postal_code"
                hintText={this.props.t('postal_code')}
                floatingLabelText={this.props.t('postal_code')}
                style={{ width: '40%', marginLeft: '10%' }}
              />

              <Field
                component={TextField}
                name="altitude"
                hintText={this.props.t('altitude')}
                floatingLabelText={this.props.t('altitude')}
                fullWidth
              />

              <PacketboxStatusFields values={packetbox} />
              {packetbox &&
                !packetbox.is_active && (
                  <RaisedButton
                    onClick={this.props.onRegenerateTan}
                    label={this.props.t('regenerate_tan')}
                  />
                )}
            </div>
          </div>
          <div className="col-xs-6">
            <div className="box">
              <Field
                component={TextField}
                name="lora___lora_app_eui"
                disabled
                hintText={this.props.t('lora_app_eui')}
                floatingLabelText={this.props.t('lora_app_eui')}
                fullWidth
              />

              <Field
                component={TextField}
                name="lora___lora_app_key"
                disabled
                hintText={this.props.t('lora_app_key')}
                floatingLabelText={this.props.t('lora_app_key')}
                fullWidth
              />

              <Field
                component={TextField}
                name="lora___lora_dev_eui"
                disabled
                hintText={this.props.t('lora_dev_eui')}
                floatingLabelText={this.props.t('lora_dev_eui')}
                fullWidth
              />

              <Field
                name="lora___lora_provider"
                disabled
                component={SelectField}
                hintText={this.props.t('lora_provider')}
                fullWidth
                floatingLabelText={this.props.t('lora_provider')}
              >
                <MenuItem value="SLS" primaryText="SLS" />
                <MenuItem value="LORIOT" primaryText="Loriot" />
                <MenuItem value="FIREFLY" primaryText="Firefly" />
                <MenuItem value="SWISSCOM" primaryText="Swisscom" />
                <MenuItem value="TTN" primaryText="TTN" />
              </Field>

              <Field
                component={TextField}
                name="alert_frequency_days"
                hintText={this.props.t('alert_frequency_days')}
                floatingLabelText={this.props.t('alert_frequency_days')}
                fullWidth
              />

              <PlainField>
                {this.props.t('alert_last_datetime')}:{' '}
                {(packetbox && dateFromISO(packetbox.alert_last_datetime)) || 'N/A'}
              </PlainField>
            </div>
          </div>
        </div>

        <ButtonContainer>
          <Link onClick={browserHistory.goBack}>
            <RaisedButton label={this.props.t('back')} />
          </Link>

          <StyledSubmitButton label={this.props.t('save')} type="submit" primary />
        </ButtonContainer>

        <ChangeOwnerDialog
          open={this.state.changeOwnerDialogOpen}
          onConfirm={this.changeOwner}
          currentOwner={this.state.owner}
          endUsers={endUsers}
          packetbox={packetbox}
          packetboxKeys={packetboxKeys}
          onCancel={this.closeChangeOwnerDialog}
        />
      </form>
    );
  }
}

PacketboxForm.propTypes = propTypes;
PacketboxForm.defaultProps = defaultProps;

const extended = translate(['translations'], { i18n })(PacketboxForm);
export default reduxForm({ form: 'packetbox' })(localize(extended, 'locale'));



// WEBPACK FOOTER //
// ./src/components/PacketboxForm.js